import ChatCard from "components/DesignSystem/ChatCard/v2/ChatCard";
import { useRoleBasedView } from "hooks/useRoleBasedView";

import {
  CALL_CONNECTED,
  CALL_DISCONNECTED,
  CALL_NO_RESPONSE,
} from "constants/CallAdmin";
import CallReceive from "static/images/AdminCallAccept.svg";
import CallReject from "static/images/AdminCallReject.svg";
import CallUnresponse from "static/images/AdminCallUnresponse.svg";

import { Button } from "components/DesignSystem/Button/Button";
import { CallSummaryCardLink } from "components/CallSummary/CallSummaryCardLink";
import { CallNotesModal } from "components/MessageInput/AdminCall/CallNotesModal";
import { useModal } from "hooks/useModal";
import { useGetCallDetailsQuery } from "store/apis/chat";

import authContext from "jwt_context&axios/authContext";
import { useContext } from "react";

const CallButton = ({
  onClick,
  groupUserPhoneCallId,
  isOpen,
  close,
  btnText,
  viewOnly,
}: {
  onClick: () => void;
  groupUserPhoneCallId: string;
  isOpen: boolean;
  close: () => void;
  btnText: string;
  viewOnly: boolean;
}) => {
  return (
    <>
      <Button size="small" customType="primary" block onClick={onClick}>
        {viewOnly ? "View Notes" : "Edit Notes"}
      </Button>
      <CallNotesModal
        isOpen={isOpen}
        close={close}
        viewOnly={viewOnly}
        groupUserPhoneCallId={groupUserPhoneCallId}
      />
    </>
  );
};

const AdminCallCardBtn = ({
  status,
  callerProfileId,
  groupUserPhoneCallId,
}: {
  status: string;
  callerProfileId: string;
  groupUserPhoneCallId: string;
}) => {
  const {
    authtoken: { uuid },
  } = useContext(authContext);

  const notesModal = useModal();

  if (callerProfileId !== uuid) {
    return (
      <>
        <CallButton
          isOpen={notesModal.isOpen}
          close={notesModal.close}
          groupUserPhoneCallId={groupUserPhoneCallId}
          btnText="View Notes"
          onClick={notesModal.open}
          viewOnly
        />
      </>
    );
  } else if (
    status === CALL_DISCONNECTED ||
    status === CALL_CONNECTED ||
    status === CALL_NO_RESPONSE
  ) {
    return (
      <CallButton
        isOpen={notesModal.isOpen}
        close={notesModal.close}
        groupUserPhoneCallId={groupUserPhoneCallId}
        btnText="Edit Notes"
        onClick={notesModal.open}
        viewOnly={false}
      />
    );
  }
  return null;
};

const CallIcon = {
  [CALL_NO_RESPONSE]: CallUnresponse,
  [CALL_CONNECTED]: CallReceive,
  [CALL_DISCONNECTED]: CallReject,
};

const CallHeader = {
  [CALL_NO_RESPONSE]: "Voice Call",
  [CALL_CONNECTED]: "Call Connected",
  [CALL_DISCONNECTED]: "Couldn’t Connect",
};

export type AdminCallCardStatusType =
  | typeof CALL_NO_RESPONSE
  | typeof CALL_CONNECTED
  | typeof CALL_DISCONNECTED;

type CustomData = {
  header: string;
  metadata: {
    call_log: {
      call_status: AdminCallCardStatusType;
      caller: { name: string; profile_id: string };
      receiver: { name: string; profile_id: string };
      uuid: string;
    };
    // Pushed onto this same card asynchronously, after the call, via the backend's
    // StreamHandler().update_message() - same lifecycle as call_log.call_status above.
    // Deliberately a separate value from CallRecording.call_summary (the CallSummary
    // object from useGetCallDetailsQuery, used only by the admin-only recordings
    // section below) - this one must stay visible to non-admins too.
    call_summary?: string | null;
  };
};

const formatDuration = (seconds: number) =>
  new Date(seconds * 1000).toISOString().slice(14, 19);

const CallRecordingsSection = ({
  groupUserPhoneCallId,
}: {
  groupUserPhoneCallId: string;
}) => {
  const { data } = useGetCallDetailsQuery(
    { groupUserPhoneCallId },
    { skip: !groupUserPhoneCallId }
  );

  const recording = data?.recordings?.[0];

  if (!recording) return null;

  return (
    <div className="t-flex t-flex-col t-gap-2 t-w-full">
      <audio
        controls
        controlsList="novolume nodownload noplaybackrate"
        src={recording.recording_url}
        className="t-h-8 t-w-full t-overflow-hidden"
      />
    </div>
  );
};

export const AdminCallCard = ({ customData }: { customData: CustomData }) => {
  const { isAdmin } = useRoleBasedView();
  const { header, metadata } = customData;
  const {
    call_log: { call_status, caller, receiver, uuid },
    call_summary,
  } = metadata;

  return (
    <ChatCard.Content addMargin={false}>
      <ChatCard.Header
        icon={<img src={CallIcon[call_status]} alt={call_status} />}
      >
        <div className="t-text-subtext-sm t-text-text-30">
          {CallHeader[call_status]}
        </div>
      </ChatCard.Header>
      <ChatCard.Body>
        <div className="t-min-w-52 t-text-subtext-sm t-text-text-60">
          {caller?.name + " (Inkle)"}
          {" <> "}
          {receiver?.name}
        </div>
        <div className="t-mt-4">
          <div className="t-text-subtext-sm t-text-text-30">{}</div>
        </div>
        {isAdmin && (
          <div className="t-flex t-flex-col t-gap-2 t-mt-4 t-w-full">
            <CallRecordingsSection groupUserPhoneCallId={uuid} />
            <AdminCallCardBtn
              key={call_status}
              status={call_status}
              callerProfileId={caller?.profile_id}
              groupUserPhoneCallId={uuid}
            />
          </div>
        )}
        <CallSummaryCardLink summary={call_summary} />
      </ChatCard.Body>
    </ChatCard.Content>
  );
};
